// Content for "Astrology & Bhakti" series, Part 5
// Class by HG Balarāma Śakti Dāsa, Bhakti Saṅgha, on SB 5.14.8 (30 October 2021)
//
// This part reproduces Section 6 of the class as it appears on the presentation
// slides — Śrīla Prabhupāda's letters, conversations, and memories on astrology.
// This section is fully written out on the slides, so it is reproduced closely below.
// No audio-only prose commentary is included.

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySlokaHeader: 'Śrīla Prabhupāda\'s Statements about the Role of Astrology in a Devotee\'s Life',
    primarySloka: {
        reference: 'Śrīla Prabhupāda, Letter to Devamaya — Bombay, 9 January 1975',
        sloka: 'Astrology is meant for the materialist, but a spiritualist does not care for the future.',
        translation:
            'This section reproduces Section 6 of the class — Śrīla Prabhupāda\'s statements about the role of astrology in a devotee\'s life — as they appear on the slides.',
    },
    blocks: [
        {
            type: 'heading',
            headingText: 'Section 6 — Śrīla Prabhupāda\'s Statements about the Role of Astrology in Devotees\' Lives',
        },
        {
            type: 'heading',
            headingText: 'Śrīla Prabhupāda\'s Letter to Devamaya — Bombay, 9 January 1975',
            content:
                'Regarding astrology, you should not listen to any of these so-called astrologers — strictly avoid. Don\'t even see them. What is the use of seeing them? <strong>Astrology is meant for the materialist, but a spiritualist does not care for the future.</strong> Everything is dependent upon Kṛṣṇa. So where is the necessity of astrology? The devotees\' principle is, let there happen anything as Kṛṣṇa desires. Let me remain sincere devotee, that\'s all. Pure devotee is never interested in this astrology. So, continue nicely with your deity worship there and read my books very carefully, and without fail chant 16 rounds on your beads daily. I hope this meets you in good health.',
        },
        {
            type: 'heading',
            headingText: 'Śrīla Prabhupāda\'s Letter to Sanātana — Honolulu, 10 June 1975',
            content:
                'My dear Sanātana dāsa, Please accept my blessings. I am in due receipt of your letter dated June 6, 1975 and have noted the contents. No, you should not bother with all this nonsense. Astrology will not save you at the time of death. My Guru Mahārāja was a great astrologer and astronomer, but he gave it all up. It is meant for the karmīs. We have no interest in such things. I hope this meets you in good health.',
        },
        {
            type: 'heading',
            headingText: 'Śrīla Prabhupāda\'s Conversation with Tamāla Kṛṣṇa Mahārāja — Los Angeles, 4 August 1969',
        },
        {
            type: 'text',
            content:
                'Tamāla Kṛṣṇa: What is the position of astrology in Kṛṣṇa consciousness?',
        },
        {
            type: 'text',
            content:
                'Prabhupāda: Astrology is a science. Kṛṣṇa consciousness has nothing to do with astrology, but it is the general custom that as soon as a child is born the astrologers come. That is the Indian system, Vedic system.',
        },
        {
            type: 'heading',
            headingText: 'Room Conversation — Vṛndāvana, 4 October 1977',
        },
        {
            type: 'text',
            content: 'Hari-śauri: Are these astrological charts very much applicable for a devotee, Śrīla Prabhupāda?',
        },
        {
            type: 'text',
            content: 'Prabhupāda: No.',
        },
        {
            type: 'text',
            content: 'Hari-śauri: Because Kṛṣṇa can do anything.',
        },
        {
            type: 'text',
            content: 'Prabhupāda: Yes. [break] Tamāla Kṛṣṇa. Tamāla Kṛṣṇa.',
        },
        {
            type: 'text',
            content: 'Tamāla Kṛṣṇa: Yes, Śrīla Prabhupāda?',
        },
        {
            type: 'text',
            content: 'Prabhupāda: Don\'t waste money for this astrology. It is useless. Better arrange as many hours as possible to chant kīrtana.',
        },
        {
            type: 'text',
            content:
                'Tamāla Kṛṣṇa: Śrīla Prabhupāda? Today we did kīrtana starting in the afternoon till the evening. So do you want more than that?',
        },
        {
            type: 'text',
            content: 'Prabhupāda: I can hear day and night.',
        },
        {
            type: 'text',
            content: 'Tamāla Kṛṣṇa: So should we arrange...? Maybe we should arrange starting in the morning going till night.',
        },
        {
            type: 'text',
            content: 'Prabhupāda: That is according to your convenience. But kīrtana is very sweet.',
        },
        {
            type: 'text',
            content: 'Hari-śauri: Last June we were doing twenty-four hours.',
        },
        {
            type: 'text',
            content:
                'Prabhupāda: That is real business. These astrologers are karmīs. We have nothing to do with the karmī.',
        },
        {
            type: 'text',
            content:
                'Hari-śauri: Their measurement of happiness and distress is how much nice wife and children they get and how much money they get.',
        },
        {
            type: 'text',
            content: 'Prabhupāda: Yes. Bhāgavata reading and kīrtana. Hmm. Hmm.',
        },
        {
            type: 'text',
            content: 'Hari-śauri: They don\'t understand that the real happiness is giving everything to Kṛṣṇa.',
        },
        {
            type: 'text',
            content: 'Prabhupāda: Yes. Don\'t waste time and money in any other way.',
        },
        {
            type: 'heading',
            headingText: 'Śrīla Prabhupāda\'s Conversation with Viśāla Prabhu',
        },
        {
            type: 'text',
            content:
                'Viśāla dāsa wanted to find an astrologer in Vṛndāvana. Without consulting Śrīla Prabhupāda, he found one who was wearing tilaka and chanting Hare Kṛṣṇa. Viśāla wanted to get something he had seen other residents of Vṛndāvana wearing — a charm you wear around your neck that has on it all the mantras of the demigods. It was supposed to counteract all the bad influence of the planets.',
        },
        {
            type: 'text',
            content:
                'The astrologer sold him one of these neckpieces, and it all sounded very nice. He told Viśāla to dip it into the Yamunā and to have an ārati at twelve noon. Soon after this, Brahmānanda Mahārāja noticed Viśāla\'s new ornament. "Viśāla," said Brahmānanda Swāmī, "what\'s that you have around your neck?" When Viśāla told him, Brahmānanda Swāmī replied, "Prabhupāda says we don\'t have to add anything to this process." At these words Viśāla became disturbed. He thought that Brahmānanda Swāmī was right, and he also felt embarrassed.',
        },
        {
            type: 'text',
            content:
                '"Why are you wearing this?" Brahmānanda Swāmī demanded. Viśāla replied that he would like to talk to Prabhupāda, and he asked if he could have an appointment. When Viśāla went to see Prabhupāda, Śrīla Prabhupāda asked him how much he had paid the astrologer and what he had said. Viśāla said the astrologer had claimed the charm would ward off the ill effects of the planets.',
        },
        {
            type: 'text',
            content:
                '<strong>"Kṛṣṇa consciousness is beyond astrology," said Prabhupāda. "If you surrender to Kṛṣṇa, with a slight kick Kṛṣṇa can annihilate 100,000 Rāhu planets."</strong> So Viśāla put aside his astrological neckpiece and just depended on Prabhupāda and Kṛṣṇa. Soon after, a godbrother showed Viśāla a letter he had received from Prabhupāda about the same subject matter: "Astrology will not save you at the time of death. My Guru Mahārāja was a great astrologer and astronomer, but he gave it all up. It is meant for the karmīs. We have no interest in such things."',
        },
        {
            type: 'heading',
            headingText: 'Śrīla Prabhupāda Memories as Shared by Haṁsadūta Prabhu',
        },
        {
            type: 'text',
            content:
                'Prabhupāda said, "Don\'t bother with astrology. Just depend on Kṛṣṇa." But one day Pradyumna, who was Prabhupāda\'s Sanskrit secretary and was fond of astrology, said, "I\'m going to go see the astrologer B. K. Gandhi. Do you want to come?" I said, "Okay," although I knew Prabhupāda wouldn\'t want me to go.',
        },
        {
            type: 'text',
            content:
                'When we got back, Prabhupāda said, "Where have you been?" I couldn\'t lie. I said, "I was with Pradyumna." "Pradyumna? Doing what?" I said, "He took me to an astrologer." "Astrology? Why?" "Well, Pradyumna . . ." He said, "Astrologers are nonsense. Why did you go? I have instructed you not to go." I said, "Pradyumna asked me to go." He said, "If Pradyumna asks you to jump off a bridge, will you jump?" "No, Prabhupāda."',
        },
        {
            type: 'text',
            content:
                'Whenever we did something that wasn\'t sanctioned, it invariably came to light.',
        },
        {
            type: 'heading',
            headingText: 'Śrīla Prabhupāda Memories as Shared by Caitanya Devī Dāsī',
        },
        {
            type: 'text',
            content:
                'In 1975, my husband, Kanupriya, went and studied astrology in London for about six months. When he came back he began doing astrology charts for devotees and he decided he should ask Śrīla Prabhupāda\'s opinion about it. He said, "Prabhupāda, I learned some astrology and I want to know if it\'s alright if I do charts for devotees?"',
        },
        {
            type: 'text',
            content:
                'Prabhupāda said, "Oh, you are doing astrology? So can you tell me when will be the date when we will buy our new building for the New York temple?" Of course my husband said, "No, Prabhupāda." Prabhupāda said, "That is because you have to study Hora Astrology." He said, "That\'s mathematics, and it takes at least ten years to learn it. My Guru Mahārāja, Śrīla Bhaktisiddhānta Sarasvatī, was a very learned astrologer. He was the last bona fide living astrologer on this planet." He said, "Doing astrology charts for devotees, you can sometimes know the future, but our philosophy is, \'Whatever will be, will be.\' We simply chant Hare Kṛṣṇa. <strong>So best you do not do astrology charts for devotees because they will start worshiping astrology and forget about Kṛṣṇa.</strong>"',
        },
    ],
};

export default content;
